import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { IFamilyDashboard } from 'src/interfaces/client'
import type { IOrgInfo } from 'src/interfaces/org'
import type { IMember } from 'src/interfaces/family'
import { useLoader } from 'src/hooks/useLoader'
import { APIS, LOADERS, SERVICES } from 'src/config/constants'
import { ClientRole } from '../types/client-role.enum'
import { axiosInstance } from 'src/utils/request'
import type { ICenter } from 'src/interfaces/center'
import type { ClientTokenPayload } from 'src/common/store'
import { useAuthStore } from 'src/common/store'

export type ClientDashboardIssue =
  | 'familyNotFound'
  | 'memberNotFound'
  | null

export const useClientContextStore = defineStore('client.context', () => {
  // ========================================
  // IMPORTED STORES
  // ========================================
  const authStore = useAuthStore()

  // ========================================
  // STATE
  // ========================================
  // Note: orgId and centerId are now managed by authStore (source of truth)
  // Access them via computed properties below
  const loggedInMember = ref<IMember>()
  const orgInfo = ref<IOrgInfo>()

  const centerInfo = ref<ICenter>()

  // TODO: TEMPORARY - Remove when backend PAD agreement API is ready
  // Tracks local PAD agreement status in memory until API is implemented
  // const tempPadAgreementSigned = ref(false)

  // ========================================
  // FAMILY DASHBOARD (merged from client.family.store)
  // ========================================
  const familyDashboard = ref<IFamilyDashboard>()

  /**
   * Tracks the specific account mismatch that prevents the client dashboard
   * from loading for the current token.
   */
  const dashboardIssue = ref<ClientDashboardIssue>(null)

  // ========================================
  // COMPUTED PROPERTIES
  // ========================================
  // Read orgId and centerId from authStore (single source of truth)
  const orgId = computed(() => authStore.currentOrgId ?? '')
  const centerId = computed(() => authStore.currentCenterId ?? '')

  const family = computed(() => familyDashboard.value?.family)
  const primaryMember = computed(() =>
    family.value?.members.find((m) => m.isPrimaryMember),
  )
  const students = computed(() => family.value?.students ?? [])
  const regsMap = computed(() => familyDashboard.value?.studentRegistrationsMap)
  const centerConfigs = computed(() => {
    return {
      creditCardEnabled:
        centerInfo.value?.isAllowParentPortalCreditCard ?? false,
      kioskEnabled: centerInfo.value?.enableAttendanceKiosk ?? false,
      journalAcknowledgmentEnabled:
        centerInfo.value?.enableIncidentAcknowledgment ?? false,
    }
  })
  const getStudent = (studentId: string) => {
    return students.value.find((s) => s.id === studentId)
  }

  /**
   * Normalize date fields from API response
   * Converts ISO datetime strings (2023-06-11T00:00:00) to date-only format (2023-06-11)
   * Creates a deep copy to avoid mutating the original object
   */
  const normalizeDates = (data: IFamilyDashboard): IFamilyDashboard => {
    const normalizeDate = (dateStr: string | undefined): string | undefined => {
      if (!dateStr) return dateStr
      // Extract just the date portion (YYYY-MM-DD) from ISO datetime
      return dateStr.split('T')[0]
    }

    // Create a deep copy to avoid mutation
    const normalized = JSON.parse(JSON.stringify(data)) as IFamilyDashboard

    // Normalize student dates
    if (normalized.family?.students) {
      normalized.family.students = normalized.family.students.map(
        (student) => ({
          ...student,
          dateOfBirth: normalizeDate(student.dateOfBirth),
        }),
      )
    }

    return normalized
  }

  const reloadFamilyDashboard = async () => {
    const {
      getByIdLoader: familyDashboardLoader,
      error: familyDashboardError,
    } =
      useLoader<IFamilyDashboard>(LOADERS.per, {
        uri: APIS.families,
        subUri: APIS.dashboard,
      })
    const familyId = (authStore.tokenPayload as ClientTokenPayload)
      ?.extension_FamilyId
    if (!familyId) {
      familyDashboard.value = undefined
      return
    }
    const result = await familyDashboardLoader(familyId)
    if (!result) {
      return {
        result: undefined,
        message: familyDashboardError.value?.message,
      }
    }

    familyDashboard.value = normalizeDates(result)
    return {
      result,
      message: undefined,
    }
  }

  const isContextLoaded = computed<boolean>(() => {
    return !!(orgId.value && centerId.value)
  })

  const isContextReady = computed<boolean>(() => {
    return !!(
      orgId.value &&
      centerId.value &&
      loggedInMember.value &&
      familyDashboard.value
    )
  })

  // ========================================
  // ACTIONS
  // ========================================

  const setLoggedInMember = (memberData: IMember) => {
    loggedInMember.value = memberData
  }

  const memberRoleMask = computed(() => {
    let role = 0
    if (loggedInMember.value?.isPrimaryMember) {
      role |= ClientRole.Primary
    }
    if (loggedInMember.value?.isPortalMember) {
      role |= ClientRole.PortalMember
    }
    if (loggedInMember.value?.isAuthorizedSponsor) {
      role |= ClientRole.Sponsor
    }
    return role
  })

  const memberHasRole = (role: ClientRole): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    return (memberRoleMask.value & role) === role
  }

  const clearContext = () => {
    // Clear client-specific data
    loggedInMember.value = undefined
    familyDashboard.value = undefined
    dashboardIssue.value = null
    orgInfo.value = undefined
    centerInfo.value = undefined
    // tempPadAgreementSigned.value = false
    // Note: orgId/centerId are managed by tenantStore, cleared via tenantStore.clearContext()
  }

  // TODO: TEMPORARY - Remove when backend PAD agreement API is ready
  /**
   * Temporarily override the PAD agreement status
   * This allows testing the agreement flow without backend API
   */
  // const setTempPadAgreementSigned = (signed: boolean) => {
  //   tempPadAgreementSigned.value = signed
  // }

  /**
   * Get PAD agreement status - checks temp flag first, then real member data
   * TODO: Remove temp logic when backend API is ready
   */
  // const hasSignedPadAgreement = computed(() => {
  //   // If temp flag is set, use it (for testing before API is ready)
  //   if (tempPadAgreementSigned.value) {
  //     return true
  //   }
  //   // Otherwise use real member data from backend
  //   return loggedInMember.value?.hasSignedPADAgreement === true
  // })

  /**
   * Fetch family dashboard and update logged-in member in the context store.
   * This was previously implemented in `useClientPortalFunctions` — moved here
   * so context-related behavior is centralized. The function will also prompt
   * for logout if the account username changed.
   */
  const fetchFamilyDashboard = async (): Promise<void> => {
    try {
      if (!authStore.user) return
      const tokenPayload = authStore.tokenPayload as ClientTokenPayload
      if (!tokenPayload) return
      dashboardIssue.value = null
      const dashboardResponse = await reloadFamilyDashboard()
      const result = dashboardResponse?.result

      if (result && result.family) {
        const loginMember = tokenPayload.extension_Id
          ? result.family.members.find(
              (m) => m.id === tokenPayload.extension_Id,
            )
          : result.family.members.find(
              (m) =>
                m.personalContactInfo?.emailAddress?.toLowerCase() ===
                authStore.userEmail?.toLowerCase(),
            )

        if (loginMember) {
          setLoggedInMember(loginMember)
          if (!tokenPayload.extension_Id) {
            authStore.overrideExtensionId(loginMember.id!)
          }
        } else {
          dashboardIssue.value = 'memberNotFound'
        }
      } else if (
        dashboardResponse?.message === 'Family does not exist.'
      ) {
        dashboardIssue.value = 'familyNotFound'
      } else if (
        dashboardResponse?.message === 'Member is not part of family'
      ) {
        dashboardIssue.value = 'memberNotFound'
      }
    } catch {
      // Leave dashboardIssue unset for unexpected failures so the user is not
      // stranded on an account-issue page for transient errors.
    }
  }

  const loadOrgInfoAsync = async (orgId: string) => {
    if (orgId) {
      const res = await axiosInstance.get(
        `${SERVICES.res}/${APIS.public}/${APIS.orgs}/${orgId}`,
      )
      orgInfo.value = res.data
    }
  }

  const loadCenterInfoAsync = async (centerId: string) => {
    if (centerId) {
      const res = await axiosInstance.get(
        `${SERVICES.res}/${APIS.centers}/${centerId}`,
        {
          headers: {
            orgId: orgId.value,
          },
        },
      )
      centerInfo.value = res.data
    }
  }

  const reloadCenterInfo = async () => {
    if (centerId.value) {
      await loadCenterInfoAsync(centerId.value)
    }
  }

  // ========================================
  // WATCHERS - Reactive Auto-Loading
  // ========================================
  /**
   * Watch tenantStore.currentOrgId and automatically load org info
   * This eliminates the need for manual loadFromStorage() calls
   * Triggers on initial load (when auth token populates tenantStore) and on org change
   */
  watch(
    () => [authStore.currentOrgId, authStore.accessToken],
    async ([newOrgId, newAccessToken]) => {
      if (newOrgId && newAccessToken) {
        await loadOrgInfoAsync(newOrgId)
      } else {
        orgInfo.value = undefined
      }
    },
    { immediate: true },
  )

  /**
   * Watch tenantStore.currentCenterId and automatically load center info
   * Triggers on initial load (when auth token populates tenantStore) and on center change
   */
  watch(
    () => [authStore.currentCenterId, authStore.accessToken],
    async ([newCenterId, newAccessToken]) => {
      if (newCenterId && newAccessToken) {
        await loadCenterInfoAsync(newCenterId)
      } else {
        centerInfo.value = undefined
      }
    },
    { immediate: true },
  )

  /**
   * Watch for authenticated user and fetch family dashboard
   * Triggers when user logs in or token is restored from storage
   */
  watch(
    () => authStore.tokenPayload,
    async (payload) => {
      if (payload && 'extension_FamilyId' in payload) {
        // User is authenticated as client
        await fetchFamilyDashboard()
      } else {
        // User logged out or not client
        familyDashboard.value = undefined
        loggedInMember.value = undefined
      }
    },
    { immediate: true },
  )

  return {
    // Computed (read from tenantStore)
    orgId,
    centerId,

    // State
    loggedInMember,
    primaryMember,
    orgInfo,
    centerInfo,
    isContextLoaded,
    isContextReady,
    centerConfigs,
    dashboardIssue,

    // Actions
    clearContext,
    fetchFamilyDashboard,

    // Family helpers (migrated from client.family.store)
    family,
    students,
    regsMap,
    getStudent,
    memberHasRole,

    reloadCenterInfo,

    // TODO: TEMPORARY - Remove when backend PAD agreement API is ready
    // hasSignedPadAgreement,
    // setTempPadAgreementSigned,
  }
})
